import React, { useEffect, useState } from 'react';

import { Radio } from '@varicent/components';
import { Field, useFormikContext } from 'formik';

import { SearchableSelectMenuItem } from 'components/models';
import SearchableSelectMenu from 'components/SearchableSelectMenu/SearchableSelectMenu';

import { ConfigurationDialogFormInput } from 'app/components/DataPanel/ConfigurationsPanel/ConfigurationDialog';
import FormTextInputGroup from 'app/components/FormFields/FormTextInputGroup/FormTextInputGroup';

import { useScope } from 'app/contexts/scopeProvider';

import { MENU_INFINITE_SCROLL_ITEM_HEIGHT } from 'app/global/variables';

import { FileTypeEnum, GetFileList_getPlanningCycleSpec_tables } from 'app/graphql/generated/graphqlApolloTypes';
import { useGetFileListLazy } from 'app/graphql/queries/getFileList';

import { ConfigurationDestTableType, ConfigurationImportMethodType, FileStatus } from 'app/models';

import block from 'utils/bem-css-modules';
import { handleActivityFileListMerge } from 'utils/helpers/listMergeUtils';
import { formatMessage } from 'utils/messages/utils';

import style from './SymonImportMethodSelectStep.module.pcss';

const b = block(style);
interface SymonImportMethodSelectStepProps {
  handleDisabledNext: (value: boolean) => void;
  existingFileIdList: string[];
}

export const SymonImportMethodSelectStep: React.FC<SymonImportMethodSelectStepProps> = ({
  handleDisabledNext,
  existingFileIdList
}) => {
  const { selectedPlanningCycle } = useScope();

  const { setFieldValue, values, errors, touched } = useFormikContext<ConfigurationDialogFormInput>();

  const [fileList, setFileList] = useState<SearchableSelectMenuItem[]>();
  const [totalRows, setTotalRows] = useState<number>();
  const [isFileListLoaded, setIsFileListLoaded] = useState<boolean>(false);

  // calculate how many rows to load on each fetch
  const viewportHeight = window.innerHeight;
  const pageSize = Math.ceil((viewportHeight * 0.2) / MENU_INFINITE_SCROLL_ITEM_HEIGHT) * 2;

  const [getFileList, { data: fileListResult, loading, fetchMore }] = useGetFileListLazy();

  const onSelectOpen = () => {
    if (!fileListResult) {
      getFileList({
        fetchPolicy: 'no-cache',
        variables: {
          planningCycleId: selectedPlanningCycle?.id,
          tableInput: {
            startRow: 1,
            endRow: pageSize,
            uploadFilter: {
              fileTypes: [FileTypeEnum.Activity],
              statuses: [FileStatus.COMPLETED]
            }
          }
        }
      });
    } else {
      const totalCount = fileListResult.getPlanningCycleSpec?.tables?.totalNumOfTables;
      setTotalRows(totalCount);
    }
  };

  const setFileListAndTotalRows = (fileListData: GetFileList_getPlanningCycleSpec_tables) => {
    if (fileListData) {
      const fileListItems = fileListData.tableList.map((table) => ({
        key: table.tableName,
        value: table.tableId
      }));

      if (fileListData.totalNumOfTables !== totalRows) {
        setTotalRows(fileListData.totalNumOfTables);
      }

      setFileList(fileListItems);
      setIsFileListLoaded(true);
    }
  };

  useEffect(() => {
    if (fileListResult && !loading) {
      setFileListAndTotalRows(fileListResult.getPlanningCycleSpec.tables);
    }
  }, [fileListResult]);

  useEffect(() => {
    const destinationTableHasError = !!errors.destinationTable;
    const isCreateMode = values.configurationImportMethodType === ConfigurationImportMethodType.CREATE;
    const isReplaceMode = values.configurationImportMethodType === ConfigurationImportMethodType.REPLACE;
    const isReplaceExistingTable = isReplaceMode && values.destinationTableType === ConfigurationDestTableType.EXISTING;
    const isReplaceNewTable = isReplaceMode && values.destinationTableType === ConfigurationDestTableType.NEW;
    const tableNameNotEmpty = values.destinationTable.length > 0;

    if (
      (isCreateMode && tableNameNotEmpty && !destinationTableHasError) ||
      (isReplaceExistingTable && values.destinationTableId.value.length > 0) ||
      (isReplaceNewTable && tableNameNotEmpty && !destinationTableHasError)
    ) {
      handleDisabledNext(false);
    } else {
      handleDisabledNext(true);
    }
  }, [values, errors]);

  return (
    <div className={b('')} data-testid="symon-import-method-select-step">
      <div className={b('sectionTitle')}>{formatMessage('IMPORT_METHOD')}</div>
      {formatMessage('IMPORT_METHOD_DESCRIPTION')}
      <div className={b('radioContainer')}>
        <div className={b('radioItem')}>
          <Radio
            data-testid="create-configuration-radio"
            checked={values.configurationImportMethodType === ConfigurationImportMethodType.CREATE}
            value={ConfigurationImportMethodType.CREATE}
            onChange={(e) => setFieldValue('configurationImportMethodType', e.currentTarget.value)}
            labelElement={<span className={b('radioLabel')}>{formatMessage('CREATE_NEW_TABLE')}</span>}
          />
        </div>
        <div className={b('radioItem')}>
          <Radio
            data-testid="replace-configuration-radio"
            checked={values.configurationImportMethodType === ConfigurationImportMethodType.REPLACE}
            value={ConfigurationImportMethodType.REPLACE}
            onChange={(e) => setFieldValue('configurationImportMethodType', e.currentTarget.value)}
            labelElement={<span className={b('radioLabel')}>{formatMessage('REPLACE_TABLE')}</span>}
          />
        </div>
      </div>
      <div className={b('destinationFieldContainer')}>
        <label htmlFor="dataTableType" className={b('sectionTitle')}>
          {formatMessage('DESTINATION')}
        </label>
        <div data-testid="import-config-message">{formatMessage('IMPORT_DATA_DEFINITION_MESSAGE')}</div>
        {values.configurationImportMethodType === ConfigurationImportMethodType.CREATE ? (
          <div className={b('destinationInput')} data-testid="destination-table-input">
            <Field
              label={formatMessage('TABLE_NAME')}
              name="destinationTable"
              type="text"
              component={FormTextInputGroup}
              placeHolder={formatMessage('ENTER_TABLE_NAME')}
            />
          </div>
        ) : (
          <div className={b('destinationSelectionContainer')} data-testid="destination-select-container">
            <div
              className={b('destinationSelectionItem', {
                selected: values.destinationTableType === ConfigurationDestTableType.EXISTING
              })}
            >
              <Radio
                data-testid="create-configuration-radio"
                checked={values.destinationTableType === ConfigurationDestTableType.EXISTING}
                value={ConfigurationDestTableType.EXISTING}
                onChange={(e) =>
                  setFieldValue('destinationTableType', e.currentTarget.value as ConfigurationDestTableType)
                }
                labelElement={<span className={b('radioLabel')}>{formatMessage('EXISTING_TABLE')}</span>}
              />
              <div className={b('selectMenuContainer')}>
                <Field
                  name="destinationTableId"
                  items={fileList}
                  component={SearchableSelectMenu}
                  shouldDisableSearch
                  showErrors={false}
                  shouldSupportInfiniteScroll
                  initialLoadingComplete={isFileListLoaded}
                  onSelectOpen={onSelectOpen}
                  disabledItems={existingFileIdList.map((fileId) => ({
                    key: fileId,
                    value: fileId
                  }))}
                  disabled={values.destinationTableType !== ConfigurationDestTableType.EXISTING}
                  infiniteScrollProps={{
                    onFetchMore: (startRow, endRow) =>
                      fetchMore({
                        variables: {
                          planningCycleId: selectedPlanningCycle?.id,
                          tableInput: {
                            startRow,
                            endRow,
                            uploadFilter: {
                              fileTypes: [FileTypeEnum.Activity],
                              statuses: [FileStatus.COMPLETED]
                            }
                          }
                        },
                        updateQuery: (previousResult, { fetchMoreResult }) => {
                          if (!fetchMoreResult) return previousResult;
                          return handleActivityFileListMerge(previousResult, fetchMoreResult);
                        }
                      }),
                    totalRows,
                    pageSize,
                    isLoading: loading
                  }}
                  placeHolderText={formatMessage('SELECT')}
                  theme="default"
                  onSelectItem={(item) => {
                    const selectedItem = item.selectedItem;

                    setFieldValue('destinationTableId', selectedItem);
                  }}
                  showIconInField={false}
                />
              </div>
            </div>
            <div
              className={b('destinationSelectionItem', {
                selected: values.destinationTableType === ConfigurationDestTableType.NEW
              })}
            >
              <Radio
                data-testid="create-configuration-radio"
                checked={values.destinationTableType === ConfigurationDestTableType.NEW}
                value={ConfigurationDestTableType.NEW}
                onChange={(e) =>
                  setFieldValue('destinationTableType', e.currentTarget.value as ConfigurationDestTableType)
                }
                labelElement={<span className={b('radioLabel')}>{formatMessage('NEW_TABLE')}</span>}
              />
              <div className={b('destinationSelectionItemInput')} data-testid="destination-table-input">
                <Field
                  name="destinationTable"
                  type="text"
                  component={FormTextInputGroup}
                  placeHolder={formatMessage('ENTER_TABLE_NAME')}
                  showErrors={!!touched.destinationTable && !!errors.destinationTable}
                  disabled={values.destinationTableType !== ConfigurationDestTableType.NEW}
                  shouldValidateOnTouch={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
